/*
 * Slideshow — one script for the hero crossfade and the before/after scene carousels.
 *
 * The markup decides how a slide looks (stacked crossfade or one-at-a-time); this file only
 * moves `is-active` between `[data-slide]` children and keeps the controls in sync. Without it
 * the first slide is the one rendered active, so the page still shows a complete picture.
 *
 * Autoplay rules — the reason this is more than a setInterval:
 *   - Off under prefers-reduced-motion, and off in the theme editor.
 *   - Stops for good once the visitor takes control (arrow, thumbnail, dragging a wipe).
 *     A comparison that changes scene mid-drag is the failure that made autoplay a question.
 *   - Pauses while hovered, while focus is inside, while off-screen and while the tab is hidden.
 *   - A visible pause/play button, because content that moves on its own for more than five
 *     seconds needs one (WCAG 2.2.2).
 */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var inEditor = window.Shopify && window.Shopify.designMode;

  var init = function (root) {
    if (root.dataset.slideshowWired === '1') return;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    if (slides.length < 2) return;
    root.dataset.slideshowWired = '1';

    var interval = parseInt(root.dataset.interval, 10) || 6000;
    var toggle = root.querySelector('[data-slide-toggle]');
    var count = root.querySelector('[data-slide-count]');
    var caption = root.querySelector('[data-slide-caption]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slide-to]'));
    var current = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('is-active'); }));

    // With autoplay unavailable nothing moves on its own, so there is nothing to pause.
    var canPlay = !reduceMotion.matches && !inEditor && root.dataset.autoplay === 'true';
    var playing = canPlay;
    var holds = { hover: false, focus: false, offscreen: false, hidden: document.hidden };
    var timer = null;

    // Lazy images in a display:none slide are never fetched, so the next slide would open on
    // an empty frame. Promote the following slide's images once the current one is up.
    var warm = function (i) {
      slides[i].querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    };

    var show = function (i) {
      var next = (i + slides.length) % slides.length;
      if (next === current) return;
      slides[current].classList.remove('is-active');
      slides[current].setAttribute('aria-hidden', 'true');
      slides[current].inert = true;
      current = next;
      var slide = slides[current];
      warm(current);
      // Every scene opens at the even split, whatever the previous one was dragged to.
      slide.querySelectorAll('.ba__range').forEach(function (range) {
        range.value = 50;
        var frame = range.closest('.ba__frame');
        if (frame) frame.style.setProperty('--ba-pos', '50%');
      });
      slide.classList.add('is-active');
      slide.removeAttribute('aria-hidden');
      slide.inert = false;
      if (count) count.textContent = (current + 1) + ' / ' + slides.length;
      if (caption) caption.textContent = slide.dataset.label || '';
      dots.forEach(function (dot, j) {
        dot.setAttribute('aria-current', j === current ? 'true' : 'false');
      });
      warm((current + 1) % slides.length);
    };

    var schedule = function () {
      clearTimeout(timer);
      timer = null;
      if (!playing) return;
      if (holds.hover || holds.focus || holds.offscreen || holds.hidden) return;
      timer = setTimeout(function () {
        show(current + 1);
        schedule();
      }, interval);
    };

    var renderToggle = function () {
      if (!toggle) return;
      toggle.hidden = !canPlay;
      toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      toggle.dataset.state = playing ? 'playing' : 'paused';
    };

    var takeControl = function () {
      playing = false;
      renderToggle();
      schedule();
    };

    root.addEventListener('click', function (event) {
      var target = event.target.closest('[data-slide-prev], [data-slide-next], [data-slide-to], [data-slide-toggle]');
      if (!target || !root.contains(target)) return;
      if (target.hasAttribute('data-slide-toggle')) {
        playing = !playing;
        renderToggle();
        schedule();
        return;
      }
      takeControl();
      if (target.hasAttribute('data-slide-prev')) show(current - 1);
      else if (target.hasAttribute('data-slide-next')) show(current + 1);
      else show(parseInt(target.dataset.slideTo, 10));
    });

    // Dragging (or arrow-keying) a wipe is the visitor comparing — never change scene on them.
    // `isTrusted` keeps the programmatic reset in show() from counting as a visitor's input.
    root.addEventListener('input', function (event) {
      if (event.isTrusted && event.target.classList.contains('ba__range')) takeControl();
    });

    var stage = root.querySelector('[data-slide-stage]') || root;
    stage.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') { holds.hover = true; schedule(); }
    });
    stage.addEventListener('pointerleave', function () { holds.hover = false; schedule(); });
    root.addEventListener('focusin', function () { holds.focus = true; schedule(); });
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) { holds.focus = false; schedule(); }
    });
    document.addEventListener('visibilitychange', function () { holds.hidden = document.hidden; schedule(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        holds.offscreen = !entries[0].isIntersecting;
        schedule();
      }, { threshold: 0.25 }).observe(root);
    }

    slides.forEach(function (slide, j) {
      if (j !== current) { slide.setAttribute('aria-hidden', 'true'); slide.inert = true; }
    });
    warm((current + 1) % slides.length);
    renderToggle();
    schedule();
  };

  var initAll = function () {
    document.querySelectorAll('[data-slideshow]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
